import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Business Development & Promotion) ---
import {
    // Core Concepts & General
    faBullhorn,         // Promotion / Marketing / Branding
    faLightbulb,        // Strategy / Innovation / Identity
    faChartLine,        // Growth / ROI / Results / Analysis
    faUsers,            // Audience / Community / Patient Loyalty
    faBuilding,         // Hospital / Facility
    faCheckCircle,      // Credibility / Trust / Quality
    faThumbsUp,         // Satisfaction / Positive Feedback
    // 10 Comprehensive Services
    faSearchLocation,   // 1. Competitor Analysis / Market Research
    faBoxOpen,          // 2. Service Packaging
    faComments,         // 3. Patient Feedback Analysis
    faTags,             // 4. Hospital Branding & Identity
    faStar,             // 5. Image Building / Positioning
    faPrint,            // 6. Print & Collateral Design
    faLaptopCode,       // 7. Website Design & Web Content
    faMousePointer,     // 8. Digital Marketing Solutions (PPC, Social)
    faNewspaper,        // 9. Public Relations & Media Visibility
    faCalendarCheck,    // 10. Events, Promotions & Outreach
    // Why Choose Us / Value Prop
    faUserTie,          // Expertise / Experts
    faNetworkWired,     // Strong Network
    faCrosshairs,       // ROI-Driven / Targeted
    faTasks,            // Continuous Tracking / Execution
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faProjectDiagram, faMapSigns, faBullseye, faSitemap, faCalculator, faGavel,
    faHandHoldingDollar, faUsersCog, faExpandArrowsAlt, faTrophy, faDollarSign, faMedal,
    faLaptopMedical, faCloud, faBrain, faMobileAlt, faShieldAlt, faRoute, faConciergeBell,
    faAward, faChalkboardTeacher, faFileInvoiceDollar, faUserCheck, faClipboardCheck,
    faCashRegister, faWrench, faExchangeAlt, faPlayCircle, faLifeRing, faCogs, faTasks
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Importance of Branding
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor?
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Comprehensive Services (10 points)
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Measurable Results

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for marketing/branding
    const heroBgImage = 'https://images.unsplash.com/photo-1557862921-37829c790f19?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image showing presentation/strategy

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Business Development & Hospital Promotion</h1>
            <p className="cliniqor-sub-heading">
              Strategic Growth Solutions by Cliniqor – Where Healthcare Meets Innovation
            </p>
            <p className="cliniqor-intro-text">
              Promoting a hospital is crucial. Cliniqor offers strategic branding and targeted outreach to build awareness, establish credibility, generate trust, and ensure your facility isn't underutilized.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- IMPORTANCE OF BRANDING --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>The Importance of Branding & Promotion in Healthcare</h2>
              <p>Reaching the right audience and becoming the preferred provider.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>Effective branding defines identity and value, while promotion highlights services and expertise. It informs the community, inspires confidence, educates patients, and builds long-term equity and loyalty.</p>
                     <p>Regular patient inflow and optimal occupancy prove quality and satisfaction, but patients must first know and trust your hospital—this requires strategic branding and communication.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- WHY CHOOSE CLINIQOR? (Features Section - 5 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Choose Cliniqor for Hospital Branding & Promotion?</h2>
              <p>Customized, results-oriented strategies exclusively for healthcare.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (5 items) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserTie} /></div>
                    <h4>Deep Domain Expertise</h4>
                    <p>Specialized knowledge in hospital consulting and healthcare marketing.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div> {/* Icon for Team */}
                    <h4>In-house Expert Team</h4>
                    <p>Branding strategists, digital marketers, designers, communication experts.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCrosshairs} /></div>
                    <h4>Proven ROI-Driven Models</h4>
                    <p>Focus on lead generation, patient retention, and measurable visibility.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faNetworkWired} /></div>
                    <h4>Strong Media Network</h4>
                    <p>Connections in print, digital, and public relations media.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div> {/* Icon for Tracking */}
                    <h4>Adaptive Execution</h4>
                    <p>Continuous performance tracking and adaptive marketing strategies.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
            <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginTop: '20px' }}>
                 <p>We craft tailor-made strategies combining creative branding, data-backed marketing, competitor intelligence, and omnichannel outreach.</p>
            </div>
          </div>
        </section>

        {/* --- COMPREHENSIVE SERVICES (Detailed Planning Section - 10 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Comprehensive Business Development & Promotion Services</h2>
               <p>Supporting your hospital’s growth journey every step of the way.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 10 Services */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSearchLocation} /></div>
                    <div className="cliniqor-content"><h4>1. Competitor Analysis & Market Research</h4> <p>Identifying competitors, gaps, patient behavior, benchmarking for competitive advantage.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBoxOpen} /></div>
                    <div className="cliniqor-content"><h4>2. Service Packaging & Repackaging</h4> <p>Refining offerings, creating differentiated bundles, designing promotional packages.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faComments} /></div>
                    <div className="cliniqor-content"><h4>3. Patient Feedback Analysis</h4> <p>Collecting/analyzing satisfaction data to identify improvements and shape messaging.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTags} /></div>
                    <div className="cliniqor-content"><h4>4. Hospital Branding & Identity Development</h4> <p>Name refinement, tagline, messaging, logo, visual identity, signage, tone-of-voice.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faStar} /></div>
                    <div className="cliniqor-content"><h4>5. Image Building & Public Positioning</h4> <p>Highlighting clinical excellence, doctor expertise, patient stories, thought leadership, PR.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faPrint} /></div>
                    <div className="cliniqor-content"><h4>6. Print & Collateral Design</h4> <p>Brochures, catalogues, welcome kits, flyers, internal branding reflecting brand ethos.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLaptopCode} /></div>
                    <div className="cliniqor-content"><h4>7. Website Design & Web Content</h4> <p>Complete design/dev, SEO content, online booking, doctor listings, service pages.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMousePointer} /></div>
                    <div className="cliniqor-content"><h4>8. Digital Marketing Solutions</h4> <p>Google Ads, Social Media Campaigns, Local SEO, Content Marketing, ORM, Email/SMS.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faNewspaper} /></div>
                    <div className="cliniqor-content"><h4>9. Public Relations & Media Visibility</h4> <p>Press releases, media coverage, interviews, thought pieces, influencer collaborations.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCalendarCheck} /></div>
                    <div className="cliniqor-content"><h4>10. Events, Promotions & Outreach Programs</h4> <p>Health camps, screenings, workshops, corporate talks, CSR, seasonal offers.</p> </div>
                 </div>
            </div>
          </div>
        </section>

       {/* --- MEASURABLE RESULTS (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Measurable Results. Transparent Execution. Lasting Impact.</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center' }}>
                   <p>At Cliniqor, we treat branding and promotion as an investment. We monitor every campaign, evaluate results, and fine-tune strategies regularly to help you reach your revenue and visibility goals.</p>
                   <p>From launch to growth to leadership, we walk with you at every stage, transforming your hospital into a well-recognized, trusted, and patient-preferred brand.</p>
              </div>
           </div>
        </section>

        {/* Removed other potential sections like Conclusion, specific Why Choose Us layout */}

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Ready to Build a Healthcare Brand That Stands Out?</h3>
          <p>Let Cliniqor turn your hospital into a leading name. Whether launching new, rebranding, or scaling through digital outreach—we have the tools, experience, and creativity. Cliniqor – Branding Better Healthcare for a Healthier Tomorrow.</p>
          <a href="#contact" className="cliniqor-cta-button">Discuss Your Branding Needs</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;